import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, cents, day } from '../../lib'
import { Dialog, Empty, ErrorBox, Loading } from '../../app/ui'
import { SpendPage, fileLink, months, statusText } from './common'
import type { Batch } from './common'
import { SetupFooter } from '../partners/common'
import { StripeCard } from './StripePage'
import { UploadCard } from './UploadPage'

export type Pick = 'stripe' | 'upload' | 'email'

export function SpendHomePage({ pick = null }: { pick?: Pick | null }) {
  return <SpendPage>{(pid, canEdit) => <Body pid={pid} canEdit={canEdit} pick={pick} />}</SpendPage>
}

const ICON = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' } as const
function Icon({ kind }: { kind: Pick }) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" {...ICON}>
      {kind === 'stripe' && <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18M7 15h4" /></>}
      {kind === 'upload' && <><path d="M12 16V4M7 9l5-5 5 5" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" /></>}
      {kind === 'email' && <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>}
    </svg>
  )
}

const TILES: { kind: Pick; label: string; title: string }[] = [
  { kind: 'stripe', label: 'Stripe (easiest)', title: 'Stripe' },
  { kind: 'upload', label: 'Upload', title: 'Upload' },
  { kind: 'email', label: 'Email', title: 'Email' },
]

function Body({ pid, canEdit, pick }: { pid: string; canEdit: boolean; pick: Pick | null }) {
  const [help, setHelp] = useState(false)
  const [stripeDirty, setStripeDirty] = useState(false)
  const files = useQuery({
    queryKey: ['ingest', 'batches', pid],
    queryFn: () => api<{ rows: Batch[]; next_cursor: string | null }>('GET', `/api/partners/${pid}/batches`),
  })
  if (files.error) return <ErrorBox error={files.error} />
  if (!files.data) return <Loading />
  return (
    <div className="stack">
      <div className="row">
        <h1 style={{ margin: 0 }}>Sync customer spend</h1>
        <button type="button" className="help" aria-label="Help" data-testid="ingest-help" onClick={() => setHelp(true)}>?</button>
      </div>
      {help && (
        <Dialog label="How syncing works" onClose={() => setHelp(false)} testid="ingest-help-dialog">
          <p style={{ margin: 0 }}>We need what your customers pay you so we can give them points.</p>
          <ul className="help-list">
            <li><strong>Stripe:</strong> connect once.</li>
            <li><strong>Upload:</strong> upload a file at least once a month.</li>
            <li><strong>Email:</strong> set up forwarding to this inbox, or email us a file every month.</li>
          </ul>
        </Dialog>
      )}
      {pick ? (
        <section className="slip stack" data-testid={`ingest-card-${pick}`}>
          <div className="row">
            <Link to="/partner/spend" className="back" aria-label="Back" data-testid="ingest-back">←</Link>
            <h2 style={{ margin: 0 }}>{TILES.find((t) => t.kind === pick)!.title}</h2>
          </div>
          {pick === 'stripe' && <StripeCard pid={pid} canEdit={canEdit} onDirtyChange={setStripeDirty} />}
          {pick === 'upload' && <UploadCard pid={pid} canEdit={canEdit} />}
          {pick === 'email' && <EmailSetup pid={pid} canEdit={canEdit} />}
        </section>
      ) : (
        <div className="tiles">
          {TILES.map((t) => (
            <Link key={t.kind} className="tile" to={`/partner/spend/${t.kind}`} data-testid={`ingest-pick-${t.kind}`}>
              <Icon kind={t.kind} />
              <span>{t.label}</span>
            </Link>
          ))}
        </div>
      )}

      <section className="panel stack">
        <h2>Your files</h2>
        {files.data.rows.length === 0 ? (
          <Empty>No files yet.</Empty>
        ) : (
          <div className="table-wrap">
            <table data-testid="ingest-batches">
              <thead><tr><th>File</th><th>Sent</th><th>Spend</th><th>Status</th></tr></thead>
              <tbody>
                {files.data.rows.map((b) => (
                  <tr key={b.id}>
                    <td><Link to={fileLink(b.id)} data-testid="ingest-batch-link">{b.filename ?? 'File'}</Link></td>
                    <td>{day(b.received_at)}{b.channel === 'email' ? ' · email' : ''}</td>
                    <td>{b.preview?.total_cents != null && b.preview.rows > 0
                      ? `${cents(b.preview.total_cents)} · ${months(b.preview.first_month, b.preview.last_month)}` : ''}</td>
                    <td data-testid="ingest-batch-row-status">{statusText(b)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <SetupFooter pid={pid} step="spend_source" disabled={pick === 'stripe' && stripeDirty} />
    </div>
  )
}

/** Email setup is the address, a copy button and Done. Files from anyone on the team are accepted. */
function EmailSetup({ pid, canEdit }: { pid: string; canEdit: boolean }) {
  const qc = useQueryClient()
  const nav = useNavigate()
  const [copied, setCopied] = useState(false)
  const setup = useQuery({
    queryKey: ['ingest', 'email-setup', pid],
    queryFn: () => api<{ address: string; send_from: string }>('POST', `/api/partners/${pid}/email-setup`),
    staleTime: Infinity,
    enabled: canEdit,
  })
  if (!canEdit) return <p className="muted">Only partner owners and admins can set this up.</p>
  if (setup.error) return <ErrorBox error={setup.error} />
  if (!setup.data) return <Loading />
  const { address, send_from } = setup.data
  return (
    <div className="stack" data-testid="ingest-email-setup">
      <div className="prompt-box">
        <code data-testid="ingest-inbound-address">{address}</code>
        <button type="button" data-testid="ingest-email-copy"
          onClick={() => navigator.clipboard?.writeText(address).then(() => setCopied(true), () => setCopied(false))}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="muted">
        Forward your file here every month, or set up auto-forwarding. Send it from {send_from} or another address
        on your team.
      </p>
      <button type="button" className="primary" data-testid="ingest-email-done"
        onClick={() => { qc.invalidateQueries({ queryKey: ['ingest', 'sources'] }); nav('/partner/spend') }}>
        Done
      </button>
    </div>
  )
}
